import { TreeItem, SimpleTreeView } from "@mui/x-tree-view";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { createContext, FC, useContext, useMemo, useRef, useState } from "react";
import { EncryptionRule, FileColumn, FileEncryptionTemplateDto, ObfuscationDataType, ObfuscationRuleDto, ObfuscationRuleType } from "@/gen/api/encrypt";
import { OptionsData, FileDataType, MappingEditParams, Pack } from "./FileTemplateDetail.hooks";
import { JSONSchema4 } from "./jsonschema";
import { Autocomplete, Box, FormControl, FormHelperText, InputLabel, Link, MenuItem, Select, Stack, TextField as MuiTextField, Tooltip, Typography, ListSubheader } from "@mui/material";
import { SecondaryClassificationResponseDto } from "@/gen/api/governance";
import SelectiveContextProvider from "@/controller/SelectiveContextProvider";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "@/constants";
import { displayInferredType } from "@/components/settings/encryption/obfsRuleCompatibleMapping";
import { useJsonMappingViewerPresetContext } from "./FileTemplateDetail.JsonMapping.presets";
import MMOTextField from "@/utils/MockMuiOutlinedTextField";



const JsonMappingViewer: FC<{
  record:FileEncryptionTemplateDto,
  optionsData:OptionsData,
  mappingEditParams:MappingEditParams,
  errors?: Record<string, object>|null,
}> = ({ record, optionsData, mappingEditParams, errors }) => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const treeData = useMemo(() => {
    const rootSchema : JSONSchema4 = JSON.parse(record.jsonSchema || '{}')
    const treeData = buildTreeData(rootSchema, record?.columns ?? [])

    sortTree(treeData, {count: 0})
    return treeData
  }, [record?.jsonSchema, record?.columns])
  const optionsDataCached = useMemo(() => {
    const ruleMap = {
      [FileDataType.String]: ['String', ...(optionsData.ruleMap[FileDataType.String] ?? []).sort(eRuleSorter), 'Date', ...(optionsData.ruleMap[FileDataType.Date] ?? []).sort(eRuleSorter)],
      [FileDataType.Date]:   ['Date', ...(optionsData.ruleMap[FileDataType.Date] ?? []).sort(eRuleSorter), 'String', ...(optionsData.ruleMap[FileDataType.String] ?? []).sort(eRuleSorter)],
      [FileDataType.Numeric]: (optionsData.ruleMap[FileDataType.Numeric] ?? []).sort(eRuleSorter),
    } as Record<string, (EncryptionRule|string)[]>
    const clearDataRule = optionsData.obfsRules.all.find(r => r.ruleType === ObfuscationRuleType.ClearData)
    const i_arr = [
      (optionsData.obfsRules.maps[ObfuscationDataType.String] ?? [])[Symbol.iterator](),
      (optionsData.obfsRules.maps[ObfuscationDataType.DateTime] ?? [])[Symbol.iterator](),
      (optionsData.obfsRules.maps[ObfuscationDataType.Integer] ?? [])[Symbol.iterator](),
      (optionsData.obfsRules.maps[ObfuscationDataType.Numeric] ?? [])[Symbol.iterator](),
    ].map(i => {
      if (clearDataRule) { i.next() }
      return [...i]
    })
    const h = clearDataRule ? [clearDataRule] : []
    const obfsRules = {
      ...optionsData.obfsRules,
      maps: {
        [ObfuscationDataType.String]: [...h, ...i_arr[0], ...i_arr[1]],
        [ObfuscationDataType.Numeric]: [...h, ...i_arr[2], ...i_arr[3]],
        [ObfuscationDataType.DateTime]: [...h, ...i_arr[1], ...i_arr[0]],
      } as Record<ObfuscationDataType, ObfuscationRuleDto[]>
    }
    return ({optionsData: {...optionsData, ruleMap, obfsRules}})
  }, [optionsData.classifications, optionsData.ruleMap, optionsData.obfsRules])
  
  const rootNames =
    treeData.parts.flatMap((s, i) => {
      if (i === 0) { return ['Json doc root'] }
      if (s === '[*]') {
        return [' > ', <Typography key={i} variant="caption" color="InactiveCaptionText"><i>items</i>[]</Typography>]
      } else {
        return [' > ', <Typography key={i} variant="body2" component="span" >{s}</Typography>]
      }
    })

  if (!record.jsonSchema || !record.columns || record.columns.length === 0) {
    return (
      <Typography textAlign="center" margin={2}>No Data</Typography>
    )
  }
  return (
    <OptionsDataContext.Provider value={optionsDataCached as any}>
      <MappingEditParamsContext.Provider value={{ listCtx: mappingEditParams.listCtx, valueLens: mappingEditParams.valueLens, errors, version: mappingEditParams.version }}>
        <SimpleTreeView
          slots={{
            expandIcon: ChevronRightIcon,
            collapseIcon: ExpandMoreIcon,
          }}
          sx={{
            mt: '-1px',
            '& div.MuiTreeItem-content': { 
              py: 1, 
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 'unset',
            },
          }}
          expandedItems={['$']}
        >
          <TreeItem itemId="$" label={rootNames}>
            {JsonTreeView(treeData, !hasWritePermit)}
          </TreeItem>
        </SimpleTreeView>
      </MappingEditParamsContext.Provider>
    </OptionsDataContext.Provider>
  )
}

const eRuleSorter = (a: EncryptionRule, b: EncryptionRule) => a.name! < b.name! ? -1 : 1;

export default JsonMappingViewer;

const useCtxFn = () => useContext(MappingEditParamsContext)

const JsonTreeView = (tree: PathTree, disabled?:boolean) => {
  const preset = useJsonMappingViewerPresetContext()
  return (
    <SimpleTreeView
      slots={{
        expandIcon: ChevronRightIcon,
        collapseIcon: ExpandMoreIcon,
      }}
    >
      {tree.children.map(c => {
        let notApplicable = preset?.editor?.isNotApplicable(c)
        if (c.kind === 'leaf') {
          const leaf = c
          const selectorFn = (ctx: MappingEditParamsContextProps) => {
            return [
              ctx.valueLens.getValue(c.jpath || '', 'secondaryClassificationId'),
              // ctx.valueLens.getValue(c.jpath || '', 'dataType'),
              // ctx.valueLens.getValue(c.jpath || '', 'precision'),
              // ctx.valueLens.getValue(c.jpath || '', 'scale'),
              ctx.valueLens.getValue(c.jpath || '', 'encryptionRuleId'),
              ctx.valueLens.getValue(c.jpath || '', 'obfuscationRuleId'),
              c.jpath && ctx.errors && ctx.errors[c.jpath],
              ctx.version,
            ]
          }
          notApplicable = 
            notApplicable != null ? notApplicable :
              !leaf.type || ((leaf.type !== 'string') && (leaf.type !== 'number') && (leaf.type !== 'integer'))
          const labelArr = 
            leaf.part === '[*]' ? [<Typography key={0} variant="caption" color="InactiveCaptionText"> <i>items</i>[]</Typography>]
            : leaf.part.split('[*]').reduce((acc, s, i, arr) => {
              if (s === '') {
                acc.push(<Typography key={i} variant="caption" color="InactiveCaptionText"> []</Typography>)
              } else {
                acc.push(s)
              }
              return acc
            }, [] as any[])
          return (
            <TreeItem itemId={leaf.jpath || ''} key={leaf.jpath} 
              label={
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mr: 1 }}>
                  <Typography variant="subtitle2" sx={{ flex: notApplicable ? '1 1 0' : null }}>{labelArr}</Typography>
                  {notApplicable
                    ? <Box sx={{ flex: '4 4 0' }}>
                        <Typography variant="caption" color="warning.main">Not Applicable</Typography>
                      </Box>
                    : <SelectiveContextProvider
                        ProviderComponent={MappingEditParamsContext.Provider}
                        useContextFn={useCtxFn}
                        selector={selectorFn}
                      >
                        <JsonFieldMappingEditor jfield={leaf} disabled={disabled} />
                      </SelectiveContextProvider>
                  }
                </Stack>
              }
            >
            </TreeItem>
          )
        } else {
          const childTree = c
          const label = c.parts.reduce((acc, s, i) => {
            acc.push(s === '[*]' 
              ? <Typography key={i} variant="caption" color="InactiveCaptionText"> []</Typography>
              : (i > 0 ? '.' : '') + s)
            return acc
          }, [] as any[])
          if (notApplicable) {
            return (
              <TreeItem itemId={childTree.num+''} key={childTree.num} 
                label={
                  <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mr: 1 }}>
                    <Typography variant="subtitle2" sx={{ flex: '1 1 0' }}>{label}</Typography>
                    <Box sx={{ flex: '4 4 0' }}>
                      <Typography variant="caption" color="warning.main">Not Applicable</Typography>
                    </Box>
                  </Stack>
                }
              />)
          }
          return(
            <TreeItem itemId={childTree.num+''} key={childTree.num} label={label}>
              {JsonTreeView(childTree, disabled)}
            </TreeItem>
          )
        }
      })}
    </SimpleTreeView>
  )
}

const JsonFieldMappingEditor : FC<{ jfield: PathLeaf, disabled?: boolean }> = ({
  jfield,
  disabled,
}) => {
  const { optionsData: { classifications, clssMap, ruleMap, obfsRules } } = useContext(OptionsDataContext)
  const { listCtx, valueLens, errors } = useContext(MappingEditParamsContext)
  const columnName = jfield.jpath || ''
  const d = listCtx.data[columnName]
  const columnError : any = errors && errors[columnName]
  const dataType = jfield.type === 'string' ? 
    (jfield.part === '$numberDecimal' ? FileDataType.Numeric : FileDataType.String)
    : FileDataType.Numeric
  const rules = 
    (dataType === FileDataType.Numeric ? 
      ((jfield.part === '$maxKey' || jfield.part === '$minKey') ? [] : ruleMap[FileDataType.Numeric])
    : (jfield.format === 'date' ? ruleMap[FileDataType.Date] 
      : (jfield.part === 'base64' && jfield.jpath?.endsWith(".$binary.base64")) || (jfield.part === 'subType' && jfield.jpath?.endsWith(".$binary.subType")) ? [] : ruleMap[FileDataType.String])
    )
    ?? []
  const encryptor = rules.find(r => r.id === valueLens.getValue(columnName, 'encryptionRuleId'))

  const classificationSelectOptions = useMemo(() => {
    return [
      { text: '[None]', value: null, },
      ...classifications,
    ]
  }, [classifications])

  const obfsRId   = valueLens.getValue(columnName, 'obfuscationRuleId')
  const currObfsRule = obfsRId ? obfsRules.all.find(r => r.ruleId === obfsRId) : undefined
  const oRules = 
      jfield.format === 'date'          ? obfsRules.maps[ObfuscationDataType.DateTime]
    : dataType === FileDataType.Numeric ? ((jfield.part === '$maxKey' || jfield.part === '$minKey') 
      ? [] 
      : obfsRules.maps[ObfuscationDataType.Numeric])
    : (jfield.part === 'base64' && jfield.jpath?.endsWith(".$binary.base64")) || (jfield.part === 'subType' && jfield.jpath?.endsWith(".$binary.subType")) 
      ? [] 
      : obfsRules.maps[ObfuscationDataType.String]
  let error = (columnError && columnError['obfuscationRuleId']) || ''
  if (obfsRId && obfsRId !== 'skip') {
    error = !currObfsRule ? 'Rule has been deleted' : ''
  }

  // !! perf optimazation !!
  const [states, setStates] = useState({
    focusIn: false,
    f1: { open: false },
    f2: { open: false },
    f3: { open: false },
  })
  const { current: mmoBag } = useRef({
    timer: null as number|null,
    f1: {
      mmoInput: null as HTMLInputElement|null,
    },
    f2: {
      mmoInput: null as HTMLInputElement|null,
    },
    f3: {
      mmoInput: null as HTMLInputElement|null,
    },
    focusTarget: null as 'f1'|'f2'|'f3'|null,
  })
  
  const {focusSpy, attachMmoSpy, attachFullInputRef} = useMemo(() => {
    const buildMmoSpy = (t: 'f1'|'f2'|'f3') => ({
      focus: () => {
        setTimeout(() => {
          mmoBag.focusTarget = t
          if (mmoBag.timer) {
            window.clearTimeout(mmoBag.timer)
            mmoBag.timer = null
          }
          setStates(v => {
            return v.focusIn ? v : {...v, focusIn: true}
          })
        })
      },
      mousedown: (e: MouseEvent) => {
        if (e.button !== 0) { return }
        setStates(v => ({...v, [t]: { open: true }}))
      } 
    })
    const mmoSpies = { 
      f1: buildMmoSpy('f1'), 
      f2: buildMmoSpy('f2'), 
      f3: buildMmoSpy('f3'), 
    }
    return {
      focusSpy: (t: 'f1'|'f2'|'f3') => ({
        onOpen: () => {
          if (mmoBag.timer) {
            window.clearTimeout(mmoBag.timer)
            mmoBag.timer = null
          }
          setStates(v => {
            ['f1','f2','f3'].forEach(k => (v as any)[k].open = false)
            return {...v, focusIn: true, [t]: { open: true }}
          })
        },
        onClose: () => {
          setStates(v => {
            return {...v, [t]: { open: false }}
          })
        },
        onFocus: () => {
          if (mmoBag.timer) {
            window.clearTimeout(mmoBag.timer)
            mmoBag.timer = null
          }
          setStates(v => {
            return v.focusIn ? v : {...v, focusIn: true}
          })
        },
        onBlur: () => {
          mmoBag.timer = window.setTimeout(() => {
            setStates(v => {
              ['f1','f2','f3'].forEach(k => (v as any)[k].open = false)
              return ({...v, focusIn:false})
            })
          }, 20)
        },
      }),
      attachMmoSpy: (t: 'f1'|'f2'|'f3') => (input: HTMLInputElement|null) => {
        if (mmoBag[t].mmoInput !== input) {
          if (mmoBag[t].mmoInput) {
            mmoBag[t].mmoInput.removeEventListener('focusin', mmoSpies[t].focus)
            mmoBag[t].mmoInput.removeEventListener('mousedown', mmoSpies[t].mousedown)
          }
          mmoBag[t].mmoInput = input
          if (input) {
            input.addEventListener('focusin', mmoSpies[t].focus)
            input.addEventListener('mousedown', mmoSpies[t].mousedown)
          }
        }
      },
      attachFullInputRef: (t: 'f1'|'f2'|'f3', folkRef?: (x: HTMLInputElement|null)=>void) => (input: HTMLInputElement) => {
        folkRef && folkRef(input)
        if (mmoBag.focusTarget && mmoBag.focusTarget === t) {
          if (input.tagName === 'INPUT') {
            setTimeout(() => {
              input.focus()
            })
          } else { 
            const div : any = input.querySelector('div[role=button]')
            if (div) {
              setTimeout(() => {
                div.focus()
              })
            }
          }
          mmoBag.focusTarget = null
        }
      },
    }
  }, [mmoBag])

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: 800, ml: 'auto', my: 1, mr: 2 }}>
      <Box sx={{ width: 50 }}>
        <Typography variant="caption" color="InactiveCaptionText">Type: </Typography>
        <Typography variant="body2">{dataType === FileDataType.String ? 'String' : 'Number'}</Typography>
      </Box>
      <Box sx={{ width: 210 }}>
        {(() => {
          const currVal = clssMap[valueLens.getValue(columnName, 'secondaryClassificationId') || '']
          return (
            states.focusIn
            ? <Autocomplete 
                disabled={disabled}
                disableClearable={true}
                options={classificationSelectOptions}
                getOptionLabel={(option) => option ? (option.text || '') : ''}
                value={currVal || ''}
                open={states.f1.open}
                {...({...focusSpy('f1'), onFocus: undefined, onBlur: undefined })}
                
                renderInput={((params) =>
                  <MuiTextField 
                    label="Classification"
                    {...params} 
                    fullWidth 
                    size="small" 
                    error={Boolean(columnError && columnError['secondaryClassificationId'])}
                    helperText={columnError && columnError['secondaryClassificationId']}
                    {...{
                      inputProps: {...params.inputProps,
                        'aria-label': 'Classification',
                      }
                    }}
                    inputRef={attachFullInputRef('f1')}
                    {...({...focusSpy('f1'), onOpen: undefined, onClose: undefined})}
                  />
                )}
                onChange={(event, value, reason) => {
                  if (!disabled && reason === 'selectOption') {
                    if ((value as any)?.value === null) { // choosed '[None]'
                      valueLens.setValues(columnName, {
                        secondaryClassificationId: '_rm_' as any,
                        primaryClassification: undefined,
                        encryptionRuleId: 'skip' as any,
                        obfuscationRuleId: 'skip' as any,
                      })
                    } else {
                      const cls1 = value as SecondaryClassificationResponseDto
                      const prevpcls = valueLens.getValue(columnName, 'primaryClassification')
                      const flip = prevpcls && !cls1.primaryClassification
                      const patchVal = {
                        secondaryClassificationId: cls1.secondaryClassificationId,
                        primaryClassification: cls1.primaryClassification,
                        ...(flip && {
                          encryptionRuleId: 'skip' as any,
                          obfuscationRuleId: valueLens.getValue(columnName, 'obfuscationRuleId') ?? 'skip' as any,
                        })
                      }
                      valueLens.setValues(columnName, patchVal)
                    }
                  }
                }}
              />
            : <MMOTextField label="Classification" defaultValue={currVal?.text ?? ''} size="small" ref={attachMmoSpy('f1')} />
          )
        })()}
      </Box>
      <Box sx={{ width: 200 }}>
        {(() => {
          const currVal = valueLens.getValue(columnName, 'encryptionRuleId')
          const currOption = rules.find(r => r.id === currVal)
          return (
            states.focusIn
            ? <FormControl fullWidth>
                <InputLabel id={`select-label-${columnName}`}>Encryption Rule</InputLabel>
                <Select
                  disabled={disabled}
                  label="Encryption Rule"
                  labelId={`select-label-${columnName}`}
                  id={`select-${columnName}`}
                  error={Boolean(columnError && columnError['encryptionRuleId'])}
                  value={currVal || 'skip'}
                  onChange={(e) => valueLens.setValue(columnName, 'encryptionRuleId', (e.target.value) as any)}
                  open={states.f2.open}
                  {...focusSpy('f2')}
                  ref={attachFullInputRef('f2')}
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  <MenuItem value="skip">Skip</MenuItem>
                  {rules.map(r => (
                    ((r as any) === 'String' || (r as any) === 'Date')
                    ? <ListSubheader sx={{ bgcolor: theme => theme.palette.background.default }}>
                        <Typography color="InactiveCaptionText" fontWeight={200} fontSize={18} fontStyle="italic" marginLeft={-1}>{r} Rules:</Typography>
                      </ListSubheader>
                    : <MenuItem key={r.id} value={r.id as any}>{r.name}</MenuItem>
                  ))}
                </Select>
                {columnError && columnError['encryptionRuleId'] &&
                  <FormHelperText error>{columnError && columnError['encryptionRuleId']}</FormHelperText>}
              </FormControl>
            : <MMOTextField label="Encryption Rule" defaultValue={currOption?.name ?? 'Skip'} size="small" ref={attachMmoSpy('f2')} />
          )
        })()}
        {encryptor &&
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'primary.light', '& span': { width: 44, display: 'inline-block' } }}>
              <span>Input: </span>{encryptor.sampleInput}
            </Typography>
            <Typography variant="caption" sx={{ color: 'success.main' }}>Output: {encryptor.sampleOutput}</Typography>
          </Box>}
      </Box>
      <Box sx={{ width: 200 }}>
        {(() => {
          const currVal = valueLens.getValue(columnName, 'obfuscationRuleId')
          const currOption = oRules.find(r => r.ruleId === currVal)
          return (
            states.focusIn
            ? <FormControl fullWidth error={Boolean(error)}>
                <InputLabel id={`select-o-label-${columnName}`}>Obfuscation Rule</InputLabel>
                <Select
                  disabled={disabled}
                  label="Obfuscation Rule"
                  labelId={`select-o-label-${columnName}`}
                  id={`select-o-${columnName}`}
                  error={Boolean(error)}
                  value={currVal || 'skip'}
                  onChange={(e) => valueLens.setValue(columnName, 'obfuscationRuleId', (e.target.value) as any)}
                  open={states.f3.open}
                  {...focusSpy('f3')}
                  ref={attachFullInputRef('f3')}
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  <MenuItem value="skip">Skip</MenuItem>
                  {error && currObfsRule && <MenuItem value={currObfsRule.ruleId}>{currObfsRule.ruleName}</MenuItem>}
                  {oRules.map(r => (
                    <MenuItem key={r.ruleId} value={r.ruleId} >{r.ruleName}</MenuItem>))}
                </Select>
                {error && <FormHelperText error>{error}</FormHelperText>}
                {!error && currObfsRule?.ruleType !== ObfuscationRuleType.ClearData &&
                  <FormHelperText>{displayInferredType(currObfsRule?.inferredType as any ?? null)}</FormHelperText>}
                {currObfsRule?.description &&
                  <Tooltip title={currObfsRule?.description}>
                    <Link sx={{ ml:1.75, cursor:'pointer' }}><Typography component="p" variant="caption">Read Description</Typography></Link>
                  </Tooltip>}
              </FormControl>
            : <MMOTextField label="Obfuscation Rule" defaultValue={currOption?.ruleName ?? 'Skip'} size="small" ref={attachMmoSpy('f3')} />
          )
        })()}
      </Box>
    </Stack>
  )
}

const OptionsDataContext = createContext({
  optionsData: {} as OptionsData,
})

interface MappingEditParamsContextProps {
  listCtx: {
      data: Record<string, FileColumn>;
      ids: string[];
  };
  valueLens: {
      getValue: (name: string, piece: keyof (Pack)) => string | number | null | undefined;
      setValue: (name: string, piece: keyof (Pack), value: number | string) => void;
      setValues: (name: string, newpack: Pack) => void;
  };
  errors: Record<string, object> | null | undefined;
  version: number;
}
const MappingEditParamsContext = createContext({
  listCtx: {} as {
    data: Record<string, FileColumn>,
    ids: string[],
  },  
  valueLens: {} as {
    getValue: (name: string, piece: keyof (Pack)) => string | number | null | undefined;
    setValue: (name: string, piece: keyof (Pack), value: number | string) => void;
    setValues: (name: string, newpack: Pack) => void;
  },
  errors: null as Record<string, object>|null|undefined,
} as MappingEditParamsContextProps)

interface PathToken {
  part: string,
  isLeaf: boolean
}

export interface PathLeaf {
  kind: 'leaf',
  part: string,
  jpath?: string,
  type?: string,
  format?: string,
}

export interface PathTree {
  kind: 'tree',
  parts: string[],
  children: (PathLeaf|PathTree)[],
  num?: number,
}

export const parsePath = (x:string): PathToken[] => {
  const tokens : PathToken[] = []
  if (!x.startsWith('$')) { throw 'invalid path' }
  tokens.push({ part: '$', isLeaf: false })
  let rest = x.substring(1)
  while (!tokens[tokens.length - 1].isLeaf) {
    if (rest.startsWith('[*].')) {
      const part = '[*]'
      rest = rest.substring(4)
      const isLeaf = rest.length === 0
      tokens.push({ part, isLeaf })
    } else if (rest.startsWith("['")) {
      const i = rest.indexOf("']")
      const part = rest.substring(2, i).replace(`\\'`, "'")
      rest = rest.substring(i + 2)
      const isLeaf = rest.length === 0
      tokens.push({ part, isLeaf })
    } else if (rest.startsWith('.')) {
      rest = rest.substring(1)
    }
    else {
      const i = [rest.indexOf('.', 1), rest.indexOf('[', 1)].sort((a, b) => a - b).find(n => n > -1);
      const isLeaf = i == null;
      const part = isLeaf ? rest : rest.substring(0, i);
      rest = isLeaf ? '' : rest.substring(i ?? 0);
      tokens.push({ part: part, isLeaf: isLeaf });
    }
  }
  return tokens
}

/**
 * 
 * @example
 * var srcJsonSchema = `{
 *   "$schema": "http://json-schema.org/draft-04/schema#",
 *   "type": "object",
 *   "properties": {
 *     "hehe": { "type": "integer" },
 *     "dob": { "type": "string", "format": "date" },
 *     "child1": { "$ref": "#/definitions/Child1" },
 *     "child2": { "$ref": "#/definitions/Child1" },
 *     "arr1": {
 *       "type": "array",
 *       "items": { "$ref": "#/definitions/Arr1" }
 *     },
 *     "arr2": {
 *       "type": "array",
 *       "items": { "$ref": "#/definitions/Arr2" }
 *     },
 *     "arr3": {
 *       "type": "array",
 *       "items": { "$ref": "#/definitions/Arr3" }
 *     }
 *   },
 *   "definitions": {
 *     "Child1": {
 *       "type": "object",
 *       "properties": {
 *         "name": { "type": "string" },
 *         "age": { "type": "integer" }
 *       }
 *     },
 *     "Arr1": { "type": "integer" },
 *     "Arr2": {
 *       "type": "object",
 *       "properties": {
 *         "num1": { "type": "integer" },
 *         "num2": { "type": "integer" }
 *       }
 *     },
 *     "Arr3": {
 *       "type": "object",
 *       "properties": {
 *         "name": { "type": "string" },
 *         "age": { "type": "integer" }
 *       }
 *     }
 *   }
 * }`
 *  
 * const testpaths = [
 *   "$[*].[*].hehe",
 *   "$[*].[*].dob",
 *   "$[*].[*].child1.name",
 *   "$[*].[*].child1.age",
 *   "$[*].[*].child2.name",
 *   "$[*].[*].child2.age"
 * ]
 * 
 * var theTree: PathTree = {
 *   kind: 'tree',
 *   parts: ['$'],
 *   children: [
 *     { kind: 'leaf', part: 'hehe' },
 *     { kind: 'leaf', part: 'dob' },
 *     {
 *       kind: 'tree',
 *       parts: ['child1'],
 *       children: [
 *         { kind: 'leaf', part: 'name' },
 *         { kind: 'leaf', part: 'age' },
 *       ]
 *     },
 *     {
 *       kind: 'tree',
 *       parts: ['child2'],
 *       children: [
 *         { kind: 'leaf', part: 'name' },
 *         { kind: 'leaf', part: 'age' },
 *       ]
 *     },
 *     { kind: 'leaf', part: 'arr1[*]' },
 *     {
 *       kind: 'tree',
 *       parts: ['arr2','[*]'],
 *       children: [
 *         { kind: 'leaf', part: 'num1' },
 *         { kind: 'leaf', part: 'num2' },
 *       ]
 *     },
 *     {
 *       kind: 'tree',
 *       parts: ['arr3','[*]'],
 *       children: [
 *         { kind: 'leaf', part: 'name' },
 *         { kind: 'leaf', part: 'age' },
 *       ]
 *     },
 *   ]
 * }
 * 
 */

const buildTreeData = (rootSchema:JSONSchema4, columns:FileColumn[]): PathTree => {
  const refMap = !rootSchema.definitions ? {} : Object.keys(rootSchema.definitions).reduce((acc, k) => {
    const defs = rootSchema.definitions as { [k: string]: JSONSchema4; }
    const refKey = `#/definitions/${k}`
    const defSchema = defs[k]
    acc[refKey] = defSchema
    return acc
  }, {} as Record<string, JSONSchema4>)
  const replaceRefSite = (schema:JSONSchema4) => {
    if (schema['$ref']) {
      const refName = schema['$ref']
      schema['actualSchema'] = refMap[refName]
      return
    }
    if (schema.type === 'object') {
      Object.values(schema.properties || {}).forEach(replaceRefSite)
      return
    } else if (schema.type === 'array') {
      const itemSchema =
        schema.items instanceof Array ? schema.items[0] : schema.items
      itemSchema && replaceRefSite(itemSchema)
    }
  }
  Object.values(refMap).forEach(replaceRefSite)
  replaceRefSite(rootSchema)
  const jpaths = columns.map(x => x.columnName as string)
  const pathPartsGroups : PathParts[] = jpaths.map(s => ({ jpath: s, parts: parsePath(s).map(x => x.part) }))

  const splitPartsGroups = (pathPartsGroups: PathParts[], depth: number) => {
    return pathPartsGroups.reduce((acc, group) => {
      const part = group.parts[depth]
      if (part) { 
        const arr = acc[part] || (acc[part] = [])
        arr.push(group)
      }
      return acc
    }, {} as Record<string, PathParts[]>)
  }
  const ensureScheme = (schema: JSONSchema4): JSONSchema4 => schema['$ref'] ? schema.actualSchema : schema
  const assembleTreeLeaf = (currTree:PathTree, schema:JSONSchema4, pathPartsGroups: PathParts[], depth: number) => {
    const splittedGroups = splitPartsGroups(pathPartsGroups, depth)
    const keys = Object.keys(splittedGroups)
    
    const getNextTree = (part:string): PathTree => {
      if (keys.length === 1) {
        currTree.parts.push(part)
        return currTree
      } else {
        const next : PathTree = { kind: 'tree', parts: [part], children: [] }
        currTree.children.push(next)
        return next
      }
    }
    const getNextSchema = (part:string, currSchema?:JSONSchema4): JSONSchema4|undefined => {
      currSchema = currSchema ?? schema
      if (currSchema.type === 'object') {
        const next = (currSchema.properties || {})[part]
        return next && ensureScheme(next)
      } else if (currSchema.type === 'array') {
        return currSchema.items && ensureScheme(currSchema.items instanceof Array ? currSchema.items[0] : currSchema.items)
      } else {
        return currSchema
      }
    }

    for(const key of keys) {
      let part = key
      const partsGroup = splittedGroups[key]
      const firstGroupParts = partsGroup[0].parts
      let nextSchema = getNextSchema(part)
      if (!nextSchema) { continue; }
      if (partsGroup.length > 1 
      || (depth < firstGroupParts.length - 1 // not leaf
        && ((firstGroupParts[firstGroupParts.length - 1] !== '[*]') // allow: **.bar; deny: **.[*]
        || (depth < firstGroupParts.length - 2 || firstGroupParts[firstGroupParts.length - 2] === '[*]'))) // allow: **.[*][*]; deny: **.foo[*]
      ) {
        const nextTree : PathTree = getNextTree(part)
        assembleTreeLeaf(nextTree, nextSchema, partsGroup, depth + 1)
        continue;
      } 
      if (nextSchema.type === 'array') {
        nextSchema = getNextSchema('[*]', nextSchema)
        part = key+'[*]'
        if (!nextSchema) { continue }
      } 
      const leaf : PathLeaf = { kind: 'leaf', part: part, jpath: partsGroup[0].jpath, type: nextSchema.type as any, format: nextSchema.format }
      currTree.children.push(leaf)
    }
  }

  const rootTree : PathTree = { kind: 'tree', parts: ['$'], children: [] }
  assembleTreeLeaf(rootTree, rootSchema, pathPartsGroups, 1)
  return rootTree
}

interface PathParts {
  jpath: string,
  parts: string[],
}

const sortTree = (t: PathTree, h: {count:number}) => {
  t.num = h.count
  h.count = h.count + 1
  t.children = innerSort(t.children, h)
  t.children.filter(c => c.kind === 'tree').forEach(c => {
    (c as PathTree).num = h.count
    h.count = h.count + 1
  })
}

const innerSort = (arr: (PathLeaf|PathTree)[], h: {count:number}) => {
  return arr.sort((a, b) => {
    if (a.kind === 'leaf') {
      if (b.kind === 'tree') {
        sortTree(b, h)
        return -1
      } else {
        return (a.jpath || '') < (b.jpath || '') ? -1 : 1
      }
    } else {
      sortTree(a, h)
      if (b.kind === 'leaf') {
        return 1
      } else {
        sortTree(b, h)
        return a.parts[a.parts.length - 1] < b.parts[b.parts.length - 1] ? -1 : 1
      }
    }
  })
}
